/* eslint-disable no-console */
import { IImages } from "../store/data/types";
import { convertToBinary, IMediaFile, IPicture } from "../model/Image";
import { FileInfo, getFileController } from "../utils/native/nativeFileApi";
import { convertToJpegStrict, JpegConversionError, JpegMimeType } from "../utils/imageUtils";
import { DataActions } from "../store/data/actions";
import * as logger from "../utils/logger";
import constants from "../constants";
import { isNativeApp } from "../utils/native/nativeHelpers";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { extend } from "../utils/vanilla";
import BaseSync from "./base";
import { isDuplicateException } from "../utils/errorHandler/error.types";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfileBatcher from "../utils/performanceProfiler/performanceProfileBatcher";

const DvirJpegMaxDimension = 1000;
const DvirJpegQuality = 0.85;

interface IUploadFailure {
    error: unknown;
}

export default class MediaSync extends BaseSync {
    synchronize(profilingBatcher?: PerformanceProfileBatcher) {
        return this.createProfiledSync(MeasureName.SyncMedia, () => {
            return this.fetchData()
                .then(() => ["Media"])
                .catch(error => ["Media", error]);
        }, profilingBatcher);
    }

    /**
     * The outbound half of a sync: register the MediaFile entities and upload their bytes.
     *
     * Used by the pass sync.ts runs ahead of DVIR so a photo is on the server before the
     * inspection that references it is pushed (MYG-144154). Only the upload matters there, and
     * downloadMediaFiles would issue a getFileInfo — and possibly a DownloadMediaFile — for every
     * entry in the store, work the ordinary post-DVIR pass does anyway. Skipping it keeps the extra
     * pass proportional to what is actually owed to the server.
     */
    uploadPending(profilingBatcher?: PerformanceProfileBatcher) {
        return this.createProfiledSync(MeasureName.SyncMedia, () => {
            return this.addMediaFiles()
                .then(() => this.uploadMediaFiles())
                .then(() => ["Media"])
                .catch(error => ["Media", error]);
        }, profilingBatcher);
    }

    fetchData() {
        return new Promise<any>((resolve, reject) => {
            let mediaFiles = this.store.getState().data.images;
            if (!mediaFiles || Object.keys(mediaFiles).length < 1) {
                resolve();
                return;
            }

            this.downloadMediaFiles(mediaFiles)
            .then(() => this.addMediaFiles())
            .then(() => this.uploadMediaFiles())
            .then(resolve)
            .catch(reject);
        });
    }

    downloadMediaFiles(mediaFiles: DeepReadonly<IImages>): Promise {
        if (window.driveApp?.debug?.base64TestImage || !isNativeApp()) {
            return Promise.resolve();
        }
        let promises = [] as any;
        Object.keys(mediaFiles).forEach((key) => {
            let mediaFile = mediaFiles[key];
            let filePath = getFileController().getImageFilePathOnDevice(key);
            promises.push(
                new Promise((resolve, reject) => {
                    const downloadFile = () => {
                        this.api.call("DownloadMediaFile", {
                            mediaFile: { id: key }
                        }, binaryBlob => {
                            getFileController().createAndWriteNewFile(key, binaryBlob).then(() => {
                                let clonedMediaFile = extend(true, {}, mediaFile) as IPicture & IMediaFile;
                                clonedMediaFile.path = filePath;
                                this.store.dispatch(DataActions.setImages({...mediaFiles, [key]: clonedMediaFile }));
                                resolve();
                            }).catch(reject);
                        }, reject);
                    };

                    getFileController().getFileInfo(filePath).then((fileInfo: FileInfo) => {
                        if (fileInfo && fileInfo.name && mediaFile.path) {
                            resolve();
                        } else if (fileInfo && fileInfo.name && !mediaFile.path) {
                            let clonedMediaFile = extend(true, {}, mediaFile) as IPicture & IMediaFile;
                            clonedMediaFile.path = filePath;
                            this.store.dispatch(DataActions.setImages({...mediaFiles, [key]: clonedMediaFile }));
                            resolve();
                        } else {
                            downloadFile();
                        }
                    }).catch(() => {
                        // getFileInfo throws if file not found, so need to download it
                        downloadFile();
                    });
                })
            );
        });
        promises.map(p => p.catch(e => e));
        return Promise.all(promises);
    }

    addMediaFiles(): Promise {
        const images = this.store.getState().data.images;
        if (!images || !Object.keys(images).length) {
            return Promise.resolve();
        }
        let mediaFiles = extend(true, {}, images) as IImages;

        return new Promise((resolve) => {
            let calls = [] as any;
            let mediaData: IMediaFile[] = [];
            const type = ".jpg";
            Object.keys(mediaFiles).forEach((key) => {
                let mediaFile = mediaFiles[key];
                if (mediaFile.modified === "Add") {
                    calls.push(["Add", {
                        typeName: "MediaFile",
                        entity: { id: key,
                                  name: key + type,
                                  mediaType: constants.SUPPORTED_MEDIA_TYPES.IMAGE,
                                  fromDate: mediaFile.fromDate,
                                  toDate: mediaFile.fromDate,
                                  solutionId: constants.MEDIA_SOLUTION_ID,
                                  device: mediaFile.deviceId }
                    }]);
                    mediaData.push(mediaFile);
                }
            });
            this.api.multiCall(calls, () => {
                for (let i = 0; i < mediaData.length; i++) {
                    delete mediaData[i].modified;
                }
                this.store.dispatch(DataActions.setImages(mediaFiles));
                resolve();
            }, (errorObject) => {
                const requestIndex = errorObject.data?.requestIndex ?? 0;
                for (let j = 0; j < requestIndex; j++) {
                    delete mediaData[j].modified;
                }
                // Entity already exists on the server — clear its modified flag so the scheduler does not retry this Add on every subsequent sync cycle.
                if (isDuplicateException(errorObject) && mediaData[requestIndex]) {
                    delete mediaData[requestIndex].modified;
                }
                this.store.dispatch(DataActions.setImages(mediaFiles));
                logger.error(logger.Source.SYNC, `Problem adding media file: ${ errorObject.message }`, this.api.getUser(), errorObject);
                resolve();
            });
        });
    }

    uploadMediaFiles(): Promise {
        let mediaFiles = extend(true, {}, this.store.getState().data.images) as IImages;
        if (!mediaFiles || !Object.keys(mediaFiles).length) {
            return Promise.resolve();
        }
        let promises: Promise<void>[] = [];
        let uploadKeys: string[] = [];
        Object.keys(mediaFiles).forEach((key) => {
            let mediaFile = mediaFiles[key];
            if (!mediaFile.modified && !mediaFile.isUploadedToCloud) {
                promises.push(new Promise((resolve, reject) => {
                    if (window.driveApp?.debug?.base64TestImage || !isNativeApp()) {
                        resolve();
                        return;
                    }
                    // addMediaFiles registers every MediaFile as .jpg, so only JPEG bytes may be uploaded. A failed
                    // conversion rejects before the server is called; the photo stays pending and its file is untouched.
                    getFileController().getBase64Data(mediaFile.path)
                        .then((base64Data) => {
                            // getBase64Data resolves "" rather than rejecting when the file is missing.
                            if (!base64Data) {
                                return Promise.reject(new Error(`Photo file not found or unreadable: ${ key }`));
                            }
                            return !base64Data.toLowerCase().startsWith(`data:${ JpegMimeType }`)
                                ? convertToJpegStrict(base64Data, DvirJpegMaxDimension, DvirJpegQuality)
                                : Promise.resolve(base64Data);
                        })
                        .then((base64Data) => {
                            let cleanedBase64Data = base64Data.substring(base64Data.indexOf("base64,") + 7);
                            this.api.call(
                                "UploadMediaFile",
                                {
                                    mediaFile: { id: key }
                                },
                                () => resolve(),
                                reject,
                                false,
                                convertToBinary(atob(cleanedBase64Data)),
                                JpegMimeType
                            );
                        })
                        .catch((e) => {
                            const message = e instanceof JpegConversionError
                                ? `Problem preparing media file ${ key } for upload: ${ e.reason }`
                                : `Problem getting base64Data during upload step: ${ e.message }`;
                            logger.error(logger.Source.SYNC, message, this.api.getUser(), e);
                            reject(e);
                        });

                }));
                uploadKeys.push(key);
            }
        });

        // Settle every upload so one failure does not stop its siblings from being marked as uploaded.
        const settled = promises.map(p => p.then((): IUploadFailure | undefined => undefined, (error: unknown) => ({ error })));
        return Promise.all(settled).then((results) => {
            // Mark against the current store, not the snapshot taken above: photos may have been added or
            // removed while the uploads were in flight.
            const images = { ...this.store.getState().data.images } as IImages;
            let uploaded = false;
            results.forEach((failure, i) => {
                const key = uploadKeys[i];
                if (!failure && images[key]) {
                    images[key] = { ...images[key], isUploadedToCloud: true };
                    uploaded = true;
                }
            });
            if (uploaded) {
                this.store.dispatch(DataActions.setImages(images));
            }
            const firstFailure = results.find(failure => failure);
            if (firstFailure) {
                throw firstFailure.error;
            }
        });
    }
}